import { SocketManager } from './socket-manager';

/**
 * ReadReceiptTracker - tracks which agent messages have been read by the customer
 *
 * Uses Intersection Observer API to detect when messages become visible in the chat viewport.
 * Messages are batched and sent every second to reduce network traffic.
 *
 * IMPORTANT: Messages are only marked as read when:
 * 1. The widget chat panel is open (isWidgetOpen callback returns true)
 * 2. The message is actually visible within the chat messages container (80% threshold)
 * 3. The window has focus (document.hasFocus())
 */
export class ReadReceiptTracker {
  private socketManager: SocketManager;
  private observer: IntersectionObserver | null = null;
  private pendingReads: Set<number> = new Set();
  private batchInterval: NodeJS.Timeout | null = null;
  private observedElements: Map<number, HTMLElement> = new Map();
  private debug: boolean;
  private messagesContainer: HTMLElement | null = null;
  private isWidgetOpen: () => boolean;

  constructor(
    socketManager: SocketManager,
    debug: boolean = false,
    isWidgetOpen: () => boolean = () => true
  ) {
    this.socketManager = socketManager;
    this.debug = debug;
    this.isWidgetOpen = isWidgetOpen;
  }

  /**
   * Set the messages container element for IntersectionObserver root
   * This must be called before messages can be tracked
   */
  setMessagesContainer(container: HTMLElement): void {
    this.messagesContainer = container;
    this.initObserver();
    this.log('Messages container set, observer initialized');
  }

  /**
   * Initialize the intersection observer
   */
  private initObserver(): void {
    // Disconnect existing observer if any
    if (this.observer) {
      this.observer.disconnect();
    }

    // Create Intersection Observer with messages container as root
    // This ensures we check visibility within the chat panel, not the browser viewport
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: this.messagesContainer, // Use messages container, not browser viewport
        rootMargin: '0px',
        threshold: 0.8, // 80% of message must be visible
      }
    );

    // Start batch flush interval (every 1 second)
    if (!this.batchInterval) {
      this.batchInterval = setInterval(() => this.flushPendingReads(), 1000);
    }

    // Listen for window focus changes
    window.addEventListener('focus', () => this.onWindowFocus());

    this.log('ReadReceiptTracker observer initialized with container root');
  }

  /**
   * Handle intersection events
   */
  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    // Only process if:
    // 1. Window has focus
    // 2. Widget chat panel is open
    if (!document.hasFocus() || !this.isWidgetOpen()) {
      this.log('Skipping intersection - widget closed or no focus');
      return;
    }

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const msgId = parseInt(entry.target.getAttribute('data-msg-id') || '0', 10);
        const author = entry.target.getAttribute('data-author');

        // Only track agent messages
        if (msgId && author === 'agent') {
          this.pendingReads.add(msgId);
          this.log(`Message ${msgId} marked for read receipt (visible in chat panel)`);

          // Stop observing this element
          this.observer?.unobserve(entry.target);
          this.observedElements.delete(msgId);
        }
      }
    });
  }

  /**
   * Flush pending reads to the server
   */
  private flushPendingReads(): void {
    if (this.pendingReads.size === 0) {
      return;
    }

    // Only send if:
    // 1. Window has focus
    // 2. Widget chat panel is open
    if (!document.hasFocus() || !this.isWidgetOpen()) {
      this.log('Delaying flush - widget closed or no focus');
      return;
    }

    const messageIds = Array.from(this.pendingReads);
    this.log(`Flushing ${messageIds.length} read receipts:`, messageIds);

    this.socketManager.markMessagesAsRead(messageIds);
    this.pendingReads.clear();
  }

  /**
   * Handle window focus - re-check visible messages
   */
  private onWindowFocus(): void {
    // Only process if widget is open
    if (!this.isWidgetOpen()) {
      return;
    }

    // Re-check visible messages when window regains focus
    this.observedElements.forEach((element, msgId) => {
      if (this.isElementVisibleInContainer(element)) {
        this.pendingReads.add(msgId);
        this.observer?.unobserve(element);
        this.observedElements.delete(msgId);
      }
    });

    // Flush immediately on focus
    this.flushPendingReads();
  }

  /**
   * Check if element is visible within the messages container
   */
  private isElementVisibleInContainer(element: HTMLElement): boolean {
    if (!this.messagesContainer) {
      return false;
    }

    const containerRect = this.messagesContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Check if element is within container bounds
    const isInContainer = (
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom &&
      elementRect.height > 0 &&
      elementRect.width > 0
    );

    // Also check if at least 80% is visible
    if (isInContainer) {
      return true;
    }

    // Partial visibility check
    const visibleTop = Math.max(elementRect.top, containerRect.top);
    const visibleBottom = Math.min(elementRect.bottom, containerRect.bottom);
    const visibleHeight = visibleBottom - visibleTop;

    return visibleHeight >= elementRect.height * 0.8;
  }

  /**
   * Observe a message element
   * Call this when a new agent message is added to the DOM
   */
  observe(element: HTMLElement, msgId: number, author: string): void {
    if (author !== 'agent' || !this.observer) {
      return;
    }

    // Set data attributes for the intersection handler
    element.setAttribute('data-msg-id', msgId.toString());
    element.setAttribute('data-author', author);

    this.observedElements.set(msgId, element);
    this.observer.observe(element);

    this.log(`Now observing message ${msgId}`);
  }

  /**
   * Stop observing a message element
   */
  unobserve(element: HTMLElement): void {
    const msgId = parseInt(element.getAttribute('data-msg-id') || '0', 10);

    if (msgId && this.observedElements.has(msgId)) {
      this.observer?.unobserve(element);
      this.observedElements.delete(msgId);
    }
  }

  /**
   * Mark all visible messages as read (e.g., when opening chat)
   * Only processes if widget is actually open
   */
  markVisibleAsRead(): void {
    // Only mark as read if widget is open
    if (!this.isWidgetOpen()) {
      this.log('markVisibleAsRead skipped - widget is closed');
      return;
    }

    this.observedElements.forEach((element, msgId) => {
      if (this.isElementVisibleInContainer(element)) {
        this.pendingReads.add(msgId);
        this.observer?.unobserve(element);
        this.observedElements.delete(msgId);
      }
    });

    // Flush immediately
    this.flushPendingReads();
  }

  /**
   * Called when widget is opened - check and mark visible messages
   */
  onWidgetOpened(): void {
    this.log('Widget opened - checking visible messages');

    // Small delay to let DOM settle after opening animation
    setTimeout(() => {
      this.markVisibleAsRead();
    }, 100);
  }

  /**
   * Cleanup and destroy the tracker
   */
  destroy(): void {
    // Flush any remaining reads
    this.flushPendingReads();

    // Stop batch interval
    if (this.batchInterval) {
      clearInterval(this.batchInterval);
      this.batchInterval = null;
    }

    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Clear collections
    this.pendingReads.clear();
    this.observedElements.clear();
    this.messagesContainer = null;

    this.log('ReadReceiptTracker destroyed');
  }

  /**
   * Logging helper
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[ReadReceiptTracker]', ...args);
    }
  }
}
